import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { fetchAllPages } from '../lib/fetchAllPages';
import type { Order } from '../types';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests (fallback)
});

// Request interceptor - add Bearer token if available
api.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh for auth check and refresh endpoints
    // /auth/me failures are handled by useAuth hook (sets user to null)
    // /auth/refresh failures mean we need to re-login
    const skipRefresh =
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/me');

    // If 401 and not a skipped request, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry && !skipRefresh) {
      originalRequest._retry = true;

      try {
        const { refreshToken } = useAuthStore.getState();
        // Try to refresh the token via body (cross-origin safe)
        const { data } = await api.post('/auth/refresh', {
          refresh_token: refreshToken,
        });
        // Store new tokens
        if (data.access_token) {
          useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
        }
        // Retry the original request
        return api(originalRequest);
      } catch {
        // Refresh failed, logout and redirect
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ user: User; access_token: string; refresh_token: string; message: string }>('/auth/login', { email, password }),

  logout: () =>
    api.post('/auth/logout'),

  register: (data: { email: string; password: string; firstName: string; lastName: string }) =>
    api.post<{ message: string; user_id: string }>('/auth/register', {
      email: data.email,
      password: data.password,
      first_name: data.firstName,
      last_name: data.lastName,
    }),

  registerAdmin: (data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role: 'admin' | 'manager' | 'client';
    status?: 'active' | 'inactive' | 'pending';
    manager_id?: string | null;
    group_id?: number | null;
  }) => api.post('/auth/register/admin', data),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),

  verifyEmail: (token: string) =>
    api.post('/auth/verify-email', { token }),

  me: () =>
    api.get<{ user: User }>('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// User type for auth responses
interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'manager' | 'client';
}

// User API
export interface UserAdminDto {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'manager' | 'client';
  status: 'active' | 'inactive' | 'pending';
  email_verified: boolean;
  email_notifications: boolean;
  default_pdf_theme: string;
  manager_id: string | null;
  group_id: number | null;
  group: { id: number; name: string } | null;
  last_login_at: string | null;
  created_at: string;
}

export const userApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<{ users: UserAdminDto[]; total: number; page: number; per_page: number; pages: number }>('/users', { params }),
  get: (id: string) => api.get<UserAdminDto>(`/users/${id}`),
  update: (id: string, data: Record<string, unknown>) =>
    api.put<UserAdminDto>(`/users/${id}`, data),
};

// Client API
export const clientApi = {
  list: (params?: Record<string, unknown>) => api.get('/clients', { params }),
  get: (id: string) => api.get(`/clients/${id}`),
  create: (data: Record<string, unknown>) => api.post('/clients', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/clients/${id}`, data),
  delete: (id: string) => api.delete(`/clients/${id}`),
  getNotes: (id: string) => api.get(`/clients/${id}/notes`),
  addNote: (id: string, content: string) => api.post(`/clients/${id}/notes`, { content }),
  getMarketingPlan: (id: string) => api.get(`/clients/${id}/marketing-plan`),
  getMarketingPlanReport: (id: string, params?: { crop_year?: number; crop_type?: string; estimated_basis?: number }) =>
    api.get(`/clients/${id}/marketing-plan-report`, { params }),
  getBriefing: (id: string, cropYear?: number) =>
    api.get(`/clients/${id}/briefing`, { params: cropYear ? { crop_year: cropYear } : {} }),
  // Address CRUD (CM-004)
  getAddresses: (id: string) => api.get(`/clients/${id}/addresses`),
  createAddress: (id: string, data: Record<string, unknown>) =>
    api.post(`/clients/${id}/addresses`, data),
  updateAddress: (clientId: string, addressId: string, data: Record<string, unknown>) =>
    api.put(`/clients/${clientId}/addresses/${addressId}`, data),
  deleteAddress: (clientId: string, addressId: string) =>
    api.delete(`/clients/${clientId}/addresses/${addressId}`),
  // Notification recipients (US-29)
  listNotificationRecipients: (id: string) =>
    api.get(`/clients/${id}/notification-recipients`),
  createNotificationRecipient: (id: string, data: { email: string; label?: string | null }) =>
    api.post(`/clients/${id}/notification-recipients`, data),
  updateNotificationRecipient: (
    clientId: string,
    recipientId: string,
    data: { email?: string; label?: string | null },
  ) => api.put(`/clients/${clientId}/notification-recipients/${recipientId}`, data),
  deleteNotificationRecipient: (clientId: string, recipientId: string) =>
    api.delete(`/clients/${clientId}/notification-recipients/${recipientId}`),
};

// Order API
export const orderApi = {
  list: (params?: Record<string, unknown>, config?: { timeout?: number }) =>
    api.get('/orders', { params, ...config }),
  get: (id: string) => api.get(`/orders/${id}`),
  create: (data: Record<string, unknown>) => api.post('/orders', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/orders/${id}`, data),
  delete: (id: string) => api.delete(`/orders/${id}`),
  markDelivered: (id: string) => api.post(`/orders/${id}/deliver`),
  reviewOrder: (id: string, data: { action: 'approve' | 'reject'; reason?: string }) =>
    api.post(`/orders/${id}/review`, data),
  reassignCropType: (data: {
    client_id: string;
    crop_year: number;
    from_crop_type: string;
    to_crop_type: string;
  }) =>
    api.post<{
      orders_moved: number;
      breakeven_moved: number;
      breakeven_skipped: number;
      questionnaire_moved: number;
      questionnaire_skipped: number;
    }>('/orders/reassign-crop-type', data),
  bulkImport: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ created: number; errors: { row: number; error: string }[]; total_rows: number }>(
      '/orders/bulk-import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
};

/**
 * Fetch ALL orders matching `params`, walking every page instead of the first.
 *
 * `orderApi.list` is capped at 100 rows per page server-side, so views that read
 * only page 1 and then filter client-side undercount any client with more than
 * 100 orders. Callers that need the complete set (marketing plan, orders tab)
 * must use this rather than a single `orderApi.list` call.
 */
export async function fetchAllOrders(
  params?: Record<string, unknown>,
  config?: { timeout?: number },
): Promise<Order[]> {
  return fetchAllPages<Order>(async (page, perPage) => {
    const res = await orderApi.list({ ...params, page, per_page: perPage }, config);
    return {
      items: (res.data.orders ?? []) as Order[],
      total: (res.data.total ?? 0) as number,
    };
  });
}

// Option API
export const optionApi = {
  list: (params?: Record<string, unknown>) => api.get('/options', { params }),
  get: (id: string) => api.get(`/options/${id}`),
  create: (data: Record<string, unknown>) => api.post('/options', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/options/${id}`, data),
  delete: (id: string) => api.delete(`/options/${id}`),
  close: (id: string, data: { exit_date: string; sale_price: number }) =>
    api.post(`/options/${id}/close`, data),
  summary: (params?: Record<string, unknown>) => api.get('/options/summary', { params }),
};

// Customer Portal API (read-only, self-scoped)
export const customerApi = {
  getMyMarketingPlan: (params?: { crop_year?: number }) =>
    api.get('/me/marketing-plan', { params }),
};

// Calculator API
export const calculatorApi = {
  listBreakeven: (params?: Record<string, unknown>) => api.get('/calculators/breakeven', { params }),
  getBreakeven: (id: string) => api.get(`/calculators/breakeven/${id}`),
  createBreakeven: (data: Record<string, unknown>) => api.post('/calculators/breakeven', data),
  updateBreakeven: (id: string, data: Record<string, unknown>) => api.put(`/calculators/breakeven/${id}`, data),
  deleteBreakeven: (id: string) => api.delete(`/calculators/breakeven/${id}`),
  cloneBreakeven: (id: string) => api.post(`/calculators/breakeven/${id}/clone`),
  getProfitability: (clientId: string, params?: { crop_year?: number; crop_type?: string; estimated_basis?: number }) =>
    api.get(`/calculators/profitability/${clientId}`, { params }),
};

// Reference Data API
export const referenceDataApi = {
  getCropTypes: () => api.get('/reference-data/crop-types'),
  getSaleTypes: () => api.get('/reference-data/sale-types'),
  getBuyerLocations: () => api.get('/reference-data/buyer-locations'),
  getFuturesMonths: () => api.get('/reference-data/futures-months'),
  // Admin CRUD
  createCropType: (data: Record<string, unknown>) => api.post('/reference-data/crop-types', data),
  updateCropType: (id: number, data: Record<string, unknown>) => api.put(`/reference-data/crop-types/${id}`, data),
  deleteCropType: (id: number) => api.delete(`/reference-data/crop-types/${id}`),
  createSaleType: (data: Record<string, unknown>) => api.post('/reference-data/sale-types', data),
  updateSaleType: (id: number, data: Record<string, unknown>) => api.put(`/reference-data/sale-types/${id}`, data),
  deleteSaleType: (id: number) => api.delete(`/reference-data/sale-types/${id}`),
  createBuyer: (data: Record<string, unknown>) => api.post('/reference-data/buyer-locations', data),
  updateBuyer: (id: number, data: Record<string, unknown>) => api.put(`/reference-data/buyer-locations/${id}`, data),
  deleteBuyer: (id: number) => api.delete(`/reference-data/buyer-locations/${id}`),
  createFuturesMonth: (data: Record<string, unknown>) => api.post('/reference-data/futures-months', data),
  updateFuturesMonth: (id: number, data: Record<string, unknown>) => api.put(`/reference-data/futures-months/${id}`, data),
  deleteFuturesMonth: (id: number) => api.delete(`/reference-data/futures-months/${id}`),
  getDeliveryPoints: () => api.get('/reference-data/delivery-points'),
  getDeliveryLocations: () => api.get('/reference-data/delivery-locations'),
  createDeliveryPoint: (data: Record<string, unknown>) => api.post('/reference-data/delivery-points', data),
  updateDeliveryPoint: (id: number, data: Record<string, unknown>) => api.put(`/reference-data/delivery-points/${id}`, data),
  deleteDeliveryPoint: (id: number) => api.delete(`/reference-data/delivery-points/${id}`),
  createDeliveryLocation: (data: Record<string, unknown>) => api.post('/reference-data/delivery-locations', data),
  updateDeliveryLocation: (id: number, data: Record<string, unknown>) => api.put(`/reference-data/delivery-locations/${id}`, data),
  deleteDeliveryLocation: (id: number) => api.delete(`/reference-data/delivery-locations/${id}`),
};

// Questionnaire API
export const questionnaireApi = {
  // Q1 - Marketing Preferences
  listQ1: (params?: Record<string, unknown>) => api.get('/questionnaires/q1', { params }),
  getQ1: (id: string) => api.get(`/questionnaires/q1/${id}`),
  createQ1: (data: Record<string, unknown>) => api.post('/questionnaires/q1', data),
  updateQ1: (id: string, data: Record<string, unknown>) => api.put(`/questionnaires/q1/${id}`, data),
  deleteQ1: (id: string) => api.delete(`/questionnaires/q1/${id}`),
  // Q2 - Operations
  listQ2: (params?: Record<string, unknown>) => api.get('/questionnaires/q2', { params }),
  getQ2: (id: string) => api.get(`/questionnaires/q2/${id}`),
  createQ2: (data: Record<string, unknown>) => api.post('/questionnaires/q2', data),
  updateQ2: (id: string, data: Record<string, unknown>) => api.put(`/questionnaires/q2/${id}`, data),
  deleteQ2: (id: string) => api.delete(`/questionnaires/q2/${id}`),
};

// Dashboard API
export const dashboardApi = {
  getPulse: (cropYear?: number) =>
    api.get('/dashboard/pulse', { params: cropYear ? { crop_year: cropYear } : {} }),
  getPortfolioHeatmap: (cropYear?: number) =>
    api.get('/dashboard/portfolio-heatmap', { params: cropYear ? { crop_year: cropYear } : {} }),
};

// Market Data API
export const marketApi = {
  getPrices: () => api.get('/market/prices'),
};

// Chatbot API
export const chatbotApi = {
  stats: () => api.get<{ total_chunks: number }>('/chatbot/stats'),
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ message: string; chunks: number; total_documents: number }>(
      '/chatbot/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
};

// Chat Threads API
export const chatThreadApi = {
  list: (product?: string) => api.get('/chat-threads', { params: product ? { product } : {} }),
  create: (data: { title?: string; product?: string }) => api.post('/chat-threads', data),
  get: (id: string) => api.get(`/chat-threads/${id}`),
  update: (id: string, data: { title: string }) => api.patch(`/chat-threads/${id}`, data),
  delete: (id: string) => api.delete(`/chat-threads/${id}`),
  addMessages: (threadId: string, messages: { role: string; content: string }[]) =>
    api.post(`/chat-threads/${threadId}/messages`, messages),
};

// Beef Endorsement API
export const beefEndorsementApi = {
  list: (params?: Record<string, unknown>) => api.get('/beef/endorsements', { params }),
  get: (id: string) => api.get(`/beef/endorsements/${id}`),
  create: (data: Record<string, unknown>) => api.post('/beef/endorsements', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/beef/endorsements/${id}`, data),
  delete: (id: string) => api.delete(`/beef/endorsements/${id}`),
  bulkImport: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ created: number; skipped: number; errors: { row: number; error: string }[]; total_rows: number }>(
      '/beef/endorsements/bulk-import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
};

// Beef Insured API
export const beefInsuredApi = {
  list: (params?: Record<string, unknown>) => api.get('/beef/insureds', { params }),
  get: (id: string) => api.get(`/beef/insureds/${id}`),
  create: (data: Record<string, unknown>) => api.post('/beef/insureds', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/beef/insureds/${id}`, data),
  delete: (id: string) => api.delete(`/beef/insureds/${id}`),
};

// Beef Dashboard API
export const beefDashboardApi = {
  getStats: () => api.get('/beef/dashboard'),
};

// Report API
export const reportApi = {
  marketingPlanPdf: (clientId: string, params?: { crop_year?: number; crop_type?: string; estimated_basis?: number; customer_copy?: boolean }) =>
    api.get(`/reports/marketing-plan/${clientId}/pdf`, { params, responseType: 'blob' }),
  breakevenPdf: (calculatorId: string, params?: { customer_copy?: boolean }) =>
    api.get(`/reports/breakeven/${calculatorId}/pdf`, { params, responseType: 'blob' }),
  profitabilityPdf: (clientId: string, params?: { crop_year?: number; crop_type?: string; estimated_basis?: number; customer_copy?: boolean }) =>
    api.get(`/reports/profitability/${clientId}/pdf`, { params, responseType: 'blob' }),
  orderPdf: (orderId: string, params?: { customer_copy?: boolean }) =>
    api.get(`/reports/orders/${orderId}/pdf`, { params, responseType: 'blob' }),
  clientPerformancePdf: (clientId: string, params?: { crop_year?: number; crop_type?: string }) =>
    api.get(`/reports/client-performance/${clientId}/pdf`, { params, responseType: 'blob' }),
  optionsPdf: (clientId: string, params?: { crop_year?: number; crop_type?: string; customer_copy?: boolean }) =>
    api.get(`/reports/options/${clientId}/pdf`, { params, responseType: 'blob' }),
  bulkPdfZip: (payload: {
    client_ids: string[];
    report_type: 'marketing_plan' | 'client_performance';
    crop_year: number;
    crop_type?: string;
  }) => api.post('/reports/bulk-pdf', payload, { responseType: 'blob' }),
  search: (query: string) => api.get('/reports/search', { params: { q: query } }),
};

// US-35 - User group management
export interface GroupPermissionDto {
  permission_key: string;
  enabled: boolean;
}

export interface UserGroupDto {
  id: number;
  name: string;
  description: string | null;
  is_system: boolean;
  permissions: GroupPermissionDto[];
}

export const userGroupApi = {
  list: () => api.get<UserGroupDto[]>('/user-groups'),
  get: (id: number) => api.get<UserGroupDto>(`/user-groups/${id}`),
  update: (
    id: number,
    body: { name?: string; description?: string; permissions?: GroupPermissionDto[] },
  ) => api.patch<UserGroupDto>(`/user-groups/${id}`, body),
};
